import { create } from 'zustand';
import {
  CreateAndMapProps,
  CreateNewFieldData,
  FiltersState,
  GetInstancesByProductParams,
  Instance as SummaryInstance,
  Instance,
  MachineData,
  OperationType,
  Product,
  ResourceMapperNotificationTypes,
  ResourceType,
  SuccessfulMappingProps,
  UnresolvedResourcesOptions,
  UpdateResourcesProps,
} from '../types';
import toLower from 'lodash/toLower';
import { findMatches } from '../utils/findMatches';
import {
  getProducts,
  patchOperations,
  postIgnoreProduct,
  getMasterListProducts,
  getInstancesByProductId,
  getProductsByOperation,
} from '../api';
import flow from 'lodash/flow';
import sortBy from 'lodash/sortBy';
import fpSortBy from 'lodash/fp/sortBy';
import map from 'lodash/fp/map';
import uniqBy from 'lodash/fp/uniqBy';
import { defaultFilterState, MAX_SUPPORTED_YEAR } from '../constants/constants';
import { operationDecider } from '../../../resources/operations';
import { APIState } from 'models/APIState';
import { GetProductsResponse } from '../api/getProducts';
import { ResourceMapperStore, State, UndoOperation } from './ResourceMapperStoreTypes';
import { createNotificationsSlice } from './slices/notifications/notificationsSlice';
import { patchRelevantOperations } from '../utils/patchRelevantOperations';
import { generateIgnoreMessage } from '../utils/generateIgnoreMessage';
import { generateMappingMessage } from '../utils/generateMappingMessage';
import { getNewActiveResource } from '../utils/getNewActiveResource';
import moment from 'moment/moment';
import { getRawPropertyData } from 'models/Cropwise';
import { getCropwiseFields } from '../utils/getCropwiseFields';
import { CanonicalGraphqlTypes, getAllCanonicalIds } from 'models/Valet';
import i18n from 'i18next';
import { createAndMapHelper } from '../utils/createAndMapHelper';
import { deleteResourceMapping } from '../utils/deleteResourceMapping';
import { BubbleDownCase } from 'models/index';
import { createLoadingSlice } from './slices/loading/loadingSlice';

const initialState: State = {
  productsMasterListOrgId: null,
  productsMasterList: null,
  searchString: '',
  activeUnmappedResourceDescription: '',
  activeResourceType: ResourceType.product,
  selectedMasterProductId: null,
  filtersState: defaultFilterState,
  allUnmappedResources: [],
  unmappedResourcesByPartner: {},
  activeResource: null,
  selectableInstances: [],
  selectedInstances: [],
  instancesMap: new Map<string, Instance[]>(),
  instanceMachineDataMap: new Map<string, MachineData[]>(),
  instanceProductsMap: new Map<string, Product[]>(),
  autoMappingEnabled: false,
  undo: {},
  cropwiseProducts: [],
  allOperationIds: [],
  isGetProductsNeedsResolving: true,
};

const RESOURCE_QUERY_LIMIT = 1000;

//TODO Split store in slices divided by context, eg. FiltersSlice, PageResultSlice etc
// it would help to create smaller pieces of store.
// https://zustand.docs.pmnd.rs/guides/typescript#slices-pattern
export const useResourceMapperStore = create<ResourceMapperStore>()((set, get, mutate) => ({
  ...initialState,
  ...createNotificationsSlice(set, get, mutate),
  ...createLoadingSlice(set, get, mutate),
  loadProductsMasterList: async (orgSource?: string, orgCountry?: string) => {
    const { orgId, productsMasterListOrgId, isLoadingProductsMasterList, productsMasterList } =
      get();

    if (
      isLoadingProductsMasterList ||
      (productsMasterList?.length && orgId === productsMasterListOrgId)
    ) {
      return;
    }

    set({ isLoadingProductsMasterList: true });
    try {
      const productList = await getMasterListProducts({
        org_id: orgId,
        source: orgSource,
        country: orgCountry,
      });

      set({
        productsMasterList: sortBy(productList, 'product_name'),
        isLoadingProductsMasterList: false,
        productsMasterListOrgId: orgId,
      });
    } catch (e) {
      console.error(e);
      set({ isLoadingProductsMasterList: false });
    }
  },
  setSelectedMasterProductId: (id: string | null) => {
    set({ selectedMasterProductId: id });
  },
  setActiveUnmappedResourceDescription: (description: string) => {
    set({ activeUnmappedResourceDescription: description });
  },
  getSelectedMasterProduct: () => {
    return get().productsMasterList?.find(
      (product) => product.product_id === get().selectedMasterProductId,
    );
  },
  getFilteredProductsMasterList: (searchString: string) => {
    return (
      get().productsMasterList?.filter((product) =>
        toLower(product?.product_name).includes(toLower(searchString)),
      ) ?? []
    );
  },
  setSearchString: (searchString: string) => {
    set({ searchString: searchString });
  },
  findTopMatches: (input: string, topN: number = 3) => {
    const products = get().productsMasterList;
    if (!input || !products) return [];
    return findMatches(input, products, topN);
  },
  setAllUnmappedResources: (resources: Product[]) => {
    const resourcesByPartner = resources.reduce(
      (acc: Record<string, Product[]>, product: Product) => {
        const alias = product.aliases?.length ? product.aliases[0].system_type : 'MANUAL';
        return {
          ...acc,
          [alias]: [...(acc[alias] || []), product],
        };
      },
      {},
    );

    const sortedPartners = Object.entries(resourcesByPartner).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );

    if (sortedPartners.length && !get().selectedIntegration) {
      set({ selectedIntegration: sortedPartners[0][0] });
    }

    set({
      allUnmappedResources: resources,
      unmappedResourcesByPartner: resourcesByPartner,
      unmappedResourcesStatus: resources.length ? APIState.SUCCESS : APIState.EMPTY,
      gettingUnmappedResources: false,
    });
  },
  getFilteredUnmappedResources: () => {
    const resources = get().getUnmappedResourcesForIntegration();
    if (get().filtersState.isTankMixShown) {
      return resources;
    }
    return resources.filter((res) => res.product_type !== 'MIX');
  },
  getFirstUnmappedResource: () => {
    const resources = get().getFilteredUnmappedResources();
    if (!resources.length) return undefined;
    return resources[0];
  },
  getUnmappedResources: async (options: UnresolvedResourcesOptions) => {
    let next_token = undefined;
    let products: GetProductsResponse['data']['products'] = [];
    do {
      const response: GetProductsResponse = await getProducts({
        ...options,
        limit: RESOURCE_QUERY_LIMIT,
        next_token,
      });
      if (response.data) {
        products.push(...response.data.products);
        next_token = response.next_token;
      }
    } while (next_token);

    const resources = flow(fpSortBy('description'), uniqBy('product_id'))(products);

    get().setAllUnmappedResources(resources as unknown as Product[]);
  },
  loadUnmappedResources: async (options: UnresolvedResourcesOptions) => {
    set({
      gettingUnmappedResources: true,
      unmappedResourcesStatus: APIState.LOADING,
      allUnmappedResources: [],
    });

    const { getUnmappedResources, getFirstUnmappedResource, setActiveUnmappedResource } = get();

    await getUnmappedResources(options);

    if (!get().getUnmappedResources.length) return;
    set({ activeResourceType: ResourceType.product });
    setActiveUnmappedResource(getFirstUnmappedResource());
  },
  addUnmappedResource: (resource: Product) => {
    set({ allUnmappedResources: [resource, ...get().allUnmappedResources] });
  },
  getUnmappedResourcesForIntegration: () => {
    const { selectedIntegration, unmappedResourcesByPartner } = get();

    if (!selectedIntegration) return [];
    if (!unmappedResourcesByPartner[selectedIntegration]) {
      return [];
    }
    return unmappedResourcesByPartner[selectedIntegration];
  },
  setActiveUnmappedResource: (resource: Product | undefined) => {
    if (!resource) return;
    const { getInstancesByProductId } = get();
    set({
      activeResourceType: ResourceType.product,
      activeResource: resource,
      selectableInstances: [],
      selectedInstances: [],
    });

    void getInstancesByProductId(resource.product_id);
    set({ activeUnmappedResourceDescription: resource.description });
  },
  getInstancesByProductId: async (productId: string) => {
    set({ loadInstancesStatus: APIState.LOADING });
    const { orgId, getUnmappedResourcesDateRange, filtersState } = get();
    const productInstances = get().instancesMap.get(productId);

    if (productInstances) {
      set({
        selectableInstances: productInstances,
        loadInstancesStatus: APIState.SUCCESS,
      });
      return;
    }

    const requestOptions: GetInstancesByProductParams = {
      org_id: orgId,
      product_id: productId,
      ...getUnmappedResourcesDateRange(),
    };
    const operationType = filtersState.operationType;
    if (operationType !== OperationType.ALL) {
      requestOptions.operation_type = operationType;
    }

    const product = await getInstancesByProductId(requestOptions);

    const selectableInstances = flow(
      map((instance: any) => {
        const { parent_operation, ...restInstance } = instance;
        const { instances, ...restProduct } = product;
        return {
          ...restInstance,
          description: restProduct?.description ?? '',
          parent_operation: {
            ...parent_operation,
            operation_type: operationDecider(parent_operation.operation_types),
          },
          aliases: restProduct.aliases,
          center: restProduct.center,
          product_id: productId,
          id: parent_operation.id + '_' + productId,
          instance_id: instance.id,
        } as Instance;
      }),
      (instances) => sortBy(instances, 'parent_operation.start_date_time'),
    )(product?.instances);

    get().instancesMap.set(productId, selectableInstances);

    set({
      selectableInstances,
      loadInstancesStatus: APIState.SUCCESS,
    });
  },
  getAreFiltersApplied(): boolean {
    const { filtersState } = get();
    return (
      filtersState.isTankMixShown !== defaultFilterState.isTankMixShown ||
      filtersState.operationType !== defaultFilterState.operationType ||
      !filtersState.dateRange.start?.isSame(defaultFilterState.dateRange.start, 'day') ||
      !filtersState.dateRange.end?.isSame(defaultFilterState.dateRange.end, 'day')
    );
  },
  resetUnmappedResourceFilters() {
    set({ filtersState: defaultFilterState });
  },
  setUnmappedResourceFilters(filtersState: FiltersState) {
    set({ filtersState });
  },
  setSelectedInstances(selectedInstances) {
    set({ selectedInstances });
  },
  resetCacheMaps() {
    set({
      instancesMap: new Map<string, Instance[]>(),
      instanceMachineDataMap: new Map<string, MachineData[]>(),
      instanceProductsMap: new Map<string, Product[]>(),
    });
  },
  setAutoMappingEnabled(value) {
    set({ autoMappingEnabled: value });
  },
  setUndo(undo) {
    set({ undo });
  },
  setActiveResource(activeResource) {
    set({ activeResource: activeResource });
  },
  setInstancesMap(instancesMap) {
    set({ instancesMap });
  },
  addUndo(undo: Record<string, UndoOperation>) {
    set((state) => ({
      undo: { ...state.undo, ...undo },
    }));
  },
  updateResources: async ({ ignore = false }: UpdateResourcesProps) => {
    const {
      selectedInstances,
      setNotification,
      getSelectedMasterProduct,
      orgId,
      addUndo,
      selectedMasterProductId,
      activeResourceType,
      autoMappingEnabled,
    } = get();

    if (!selectedInstances.length) {
      setNotification(ResourceMapperNotificationTypes.NO_SELECTED_INSTANCES);
      return;
    }

    if (!selectedMasterProductId && !ignore) {
      setNotification(ResourceMapperNotificationTypes.NO_SELECTED_CROPWISE_RESOURCE);
      return;
    }

    if (!selectedMasterProductId || !activeResourceType) return;
    const referenceId = selectedInstances.map((inst: SummaryInstance) => inst.id).join('-');

    const productToMap = getSelectedMasterProduct();

    const patchOps: any[] = await patchRelevantOperations({
      autoMappingEnabled,
      selectedMasterProductId,
      selectedInstances,
      activeResourceType,
      ignore,
      productToMap,
      orgId,
    });

    const { successes, undoPatchOps } = patchOps.reduce(
      (acc, { instance, response, undo }) => {
        if (!response.error_type) {
          acc.successes.push(...instance.operation_ids);
          acc.undoPatchOps.push(...undo);
        }
        return acc;
      },
      { successes: [], undoPatchOps: [] } as { successes: any[]; undoPatchOps: any[] },
    );

    addUndo({ [referenceId]: { patches: undoPatchOps, unsaves: [] } });
    return { successes, referenceId, selectedMasterProductId, ignore };
  },
  handleSuccessfulMapping: ({
    successes,
    referenceId,
    activeResource,
    ignore = false,
  }: SuccessfulMappingProps) => {
    const {
      undo,
      selectedInstances,
      addUndo,
      setNotification,
      getSelectedMasterProduct,
      setActiveUnmappedResource,
      getFilteredUnmappedResources,
      updateTotalInstanceCountAndProduct,
    } = get();
    if (ignore) {
      const { note_type, note } = generateIgnoreMessage(
        successes,
        activeResource?.description || '',
      );
      setNotification(note_type, note);
    } else {
      const selectedMasterProduct = getSelectedMasterProduct();
      const { note_type, note } = generateMappingMessage(
        successes,
        activeResource?.description || '',
        selectedMasterProduct?.product_name || '',
        referenceId,
      );

      setNotification(note_type, note);
    }

    if (!activeResource || !successes.length) return;

    const removed_instances: any = [];

    let newActiveResource = getNewActiveResource(activeResource, getFilteredUnmappedResources());

    updateTotalInstanceCountAndProduct(activeResource);

    const isMappedAllInstances = selectedInstances?.length <= 0;

    if (isMappedAllInstances && activeResource?.product_id === activeResource.product_id) {
      setActiveUnmappedResource(newActiveResource);
    } else {
      set({ selectedInstances: [] });
    }

    set({ autoMappingEnabled: false });

    addUndo({
      [referenceId]: {
        ...undo[referenceId],
        removed_instances,
        removed_resource: activeResource,
      },
    });
  },
  updateTotalInstanceCountAndProduct(activeResource: Product | null) {
    if (!activeResource) return;
    const {
      instancesMap,
      selectedInstances,
      selectableInstances,
      updateMappedResource,
      removeUnmappedResource,
      setActiveUnmappedResource,
      getFilteredUnmappedResources,
    } = get();
    const instances = instancesMap.get(activeResource.product_id);
    const instancesCount = instances?.length ?? 0;

    const newInstanceCount = instancesCount - selectedInstances?.length;

    if (newInstanceCount > 0) {
      const mapIds = selectedInstances.map((i) => i.id);

      set({
        selectableInstances: selectableInstances.filter((i) => !mapIds.includes(i.id)),
        instancesMap: instancesMap.set(
          activeResource.product_id,
          instances?.filter((i) => !mapIds.includes(i.id)) ?? [],
        ),
      });

      updateMappedResource({
        ...activeResource,
        total_instance_count: newInstanceCount,
      });
      return;
    }

    removeUnmappedResource(activeResource);

    const newActiveResource = getNewActiveResource(activeResource, getFilteredUnmappedResources());

    if (activeResource?.product_id === activeResource.product_id) {
      setActiveUnmappedResource(newActiveResource);
    }
  },
  getUnmappedResourcesDateRange() {
    const dateRange = get().filtersState.dateRange;
    return {
      since: (dateRange.start?.startOf('day') || moment(0)).toISOString(),
      before: (dateRange.end?.endOf('day') || moment().year(MAX_SUPPORTED_YEAR)).toISOString(),
    };
  },
  updateMappedResource(changedResource: Product) {
    const { allUnmappedResources, setAllUnmappedResources } = get();
    const newResourceList = allUnmappedResources.map((_resource: Product) =>
      _resource.product_id === changedResource.product_id ? changedResource : _resource,
    );
    setAllUnmappedResources(newResourceList);
  },
  removeUnmappedResource(resource: Product) {
    const { allUnmappedResources, setAllUnmappedResources } = get();

    const newResourceList = allUnmappedResources.filter(
      (_resource: Product) => _resource.product_id !== resource.product_id,
    );
    setAllUnmappedResources(newResourceList);
  },
  clickUpdateResources: async (ignore: boolean = false) => {
    const { activeResource, selectedMasterProductId, updateResources, handleSuccessfulMapping } =
      get();

    set({ isUpdatingResources: true });

    const updatedResources = await updateResources({
      ignore,
    });

    if (!updatedResources || !activeResource || !selectedMasterProductId) {
      set({ isUpdatingResources: false });
      return;
    }
    const { successes, referenceId } = updatedResources;

    handleSuccessfulMapping({
      successes,
      referenceId,
      activeResource,
      ignore,
    });

    set({ isUpdatingResources: false });
    return updatedResources;
  },
  getPropertyData: async (org_id: string) => {
    const organizationProperties = await getRawPropertyData(org_id);
    set({ organizationProperties });
  },
  setUserId: (userId: string) => set({ userId }),
  setOrgId: (orgId: string) => set({ orgId }),
  setInstanceProductsMap: (map: Map<string, Product[]>) => set({ instanceProductsMap: map }),
  isShowFooterOptions: () => get().selectedIntegration !== 'MANUAL',
  getCropwiseResources: (params: any) => {
    if (get().activeResourceType === ResourceType.field) {
      return getCropwiseFields(params);
    }
    return Promise.resolve({ error_type: 'Missing Resource Type' });
  },
  resetProductsByOperations: () => set({ instanceProductsMap: new Map<string, Product[]>() }),
  addSelectedInstance: (instance: SummaryInstance) => {
    set((state) => ({
      selectedInstances: [...state.selectedInstances, instance],
    }));
  },
  removeSelectedInstance: (instance: Instance) => {
    set((state) => ({
      selectedInstances: state.selectedInstances.filter(({ id }) => id !== instance.id),
    }));
  },
  setIsGetProductsNeedsResolving: (value: boolean) => set({ isGetProductsNeedsResolving: value }),
  addAllResourceInstances: () => {
    const instances = get().selectableInstances || [];
    set({ selectedInstances: instances });
  },
  removeAllSelectedInstances: () => {
    set({ selectedInstances: [] });
  },
  setSelectedIntegration: (integration: string) => {
    const { setActiveUnmappedResource, getFirstUnmappedResource } = get();
    set({ selectedIntegration: integration });
    if (integration === 'MANUAL') {
      set({ activeResourceType: ResourceType.field });
    }

    setActiveUnmappedResource(getFirstUnmappedResource());
  },
  getAllOperationIds: async (canonicalGraphqlType: CanonicalGraphqlTypes, accessToken: string) => {
    const ids = await getAllCanonicalIds(accessToken, get().orgId, canonicalGraphqlType);
    const allOperationIds = flow(map((operation: { id: string }): string => operation.id))(
      ids,
    ) as string[];
    set({ allOperationIds });
    return allOperationIds;
  },
  refreshCropwiseFields: async (accessToken: string) => {
    const { getCropwiseResources, orgId } = get();

    set({ resourceSelectStatus: APIState.LOADING });

    const response: any = await getCropwiseResources({
      org_id: orgId,
      access_token: accessToken,
    });

    const resourceSelStatus = (() => {
      if (response?.error_type) return APIState.FAIL;
      if (!response.length) return APIState.EMPTY;
      return APIState.SUCCESS;
    })();

    set({ cropwiseProducts: response });
    set({ resourceSelectStatus: resourceSelStatus });
  },
  createNewFieldAndMapResource: async (fieldData: CreateNewFieldData, accessToken: string) => {
    const { setNotification, createAndMap } = get();
    setNotification(ResourceMapperNotificationTypes.CREATE_NEW_FIELD_IN_PROGRESS, {
      message: i18n.t('resource_mapper.notifications.create_new_field'),
      description: i18n.t('resource_mapper.notifications.do_not_refresh'),
    });

    return createAndMap({
      fieldData,
      accessToken,
    });
  },
  createAndMap: async ({ fieldData, accessToken }: CreateAndMapProps) => {
    const {
      orgId,
      activeResource,
      activeResourceType,
      setNotification,
      cropwiseProducts,
      updateResources,
      handleSuccessfulMapping,
    } = get();

    return createAndMapHelper({
      fieldData,
      accessToken,
      orgId,
      activeResource,
      activeResourceType,
      cropwiseProducts,
      setNotification,
      updateResources,
      handleSuccessfulMapping,
    });
  },
  resetModel: (soft?: boolean) => {
    set({
      activeResource: null,
      selectedInstances: [],
      selectedMasterProductId: null,
      cropwiseProducts: [],
      resourceSelectStatus: APIState.EMPTY,
    });

    if (!soft) {
      get().setAllUnmappedResources([]);
    }
  },
  setActiveResourceById: (id: string, accessToken: string) => {
    const {
      allUnmappedResources,
      setSearchString,
      setActiveUnmappedResource,
      refreshCropwiseFields,
    } = get();
    const resource = allUnmappedResources.find((_prod: Product) => _prod.product_id === id);
    if (!resource) return;

    setActiveUnmappedResource(resource);
    setSearchString('');

    if (resource.product_type === ResourceType.field) {
      void refreshCropwiseFields(accessToken);
    }
  },
  getProductsByOperationId: async (operationId: string, unitSystem: string) => {
    const { instanceProductsMap, orgId } = get();

    set({ loadInstanceProductsStatus: APIState.LOADING });

    const products = await getProductsByOperation({
      org_id: orgId,
      operation_id: operationId,
      unit_system: unitSystem,
    });

    instanceProductsMap.set(operationId, products || []);
    set({ instanceProductsMap });

    set({ loadInstanceProductsStatus: APIState.SUCCESS });
  },
  handleIgnoreProduct: async () => {
    const {
      setNotification,
      activeResource,
      selectedInstances,
      updateTotalInstanceCountAndProduct,
    } = get();

    set({ isLoadingIgnoreProduct: true });

    const ignoreProductsPromises = selectedInstances.map((instance: SummaryInstance) =>
      postIgnoreProduct({
        product_id: activeResource?.product_id,
        operation_id: instance.instance_id,
      }),
    );

    const responses = await Promise.allSettled(ignoreProductsPromises);

    const failure = responses.some(({ status }) => status === 'rejected');

    const notificationType = failure
      ? ResourceMapperNotificationTypes.IGNORE_PRODUCT_FAILURE
      : ResourceMapperNotificationTypes.IGNORE_PRODUCT_SUCCESS;

    !failure && activeResource && updateTotalInstanceCountAndProduct(activeResource);
    setNotification(notificationType);

    set({ isLoadingIgnoreProduct: false });
  },
  undoUpdateResources: async (referenceId: string | undefined) => {
    const {
      undo,
      orgId,
      activeResource,
      setNotification,
      allUnmappedResources,
      setUndo,
      addUnmappedResource,
      setActiveUnmappedResource,
    } = get();

    if (!referenceId) {
      return;
    }

    const currentUndo = undo[referenceId];
    if (!currentUndo?.patches?.length || !currentUndo.unsaves?.length) {
      return;
    }

    const patches = currentUndo.patches.map((body: any) => patchOperations(body, orgId));
    const unsaves = currentUndo.unsaves.map((body: any) => deleteResourceMapping(body));
    const resource = currentUndo?.removed_resource;

    setUndo({ [referenceId]: currentUndo });

    const patchResponses = await Promise.all(patches);
    await Promise.all(unsaves);

    const successes: any[] = [];
    patchResponses.forEach((response: any) => {
      if (!response.error_type) {
        successes.push(response[0]);
      }
    });

    const { note_type, note } = generateMappingMessage(
      successes,
      '',
      resource?.description || '',
      referenceId,
      true,
    );

    setNotification(note_type, note);

    const has_resource = allUnmappedResources.find(
      (res) => res.product_id === resource?.product_id,
    );
    if (!has_resource) {
      resource && addUnmappedResource(resource);
      return 'NO_RESOURCE';
    }
    if (resource?.product_id === activeResource?.product_id) {
      setActiveUnmappedResource(resource);
    }
  },
  init: async (orgId: string, userId: string) => {
    if (!orgId || !userId) return;

    const {
      getUnmappedResourcesDateRange,
      isGetProductsNeedsResolving,
      filtersState,
      gettingUnmappedResources,
      loadUnmappedResources,
    } = get();

    const dateRange = getUnmappedResourcesDateRange();

    set({ orgId });

    const options: UnresolvedResourcesOptions = {
      needs_resolving: isGetProductsNeedsResolving,
      org_id: orgId,
      user_id: userId,
      ...dateRange,
    };
    const operationType = filtersState.operationType;
    if (operationType !== OperationType.ALL) {
      options.operation_type = operationType;
    }

    if (gettingUnmappedResources) return;

    await loadUnmappedResources(options);
  },
  syncStore: (func_name: BubbleDownCase, ...args: any[]) => {
    const {
      getUnmappedResourcesDateRange,
      filtersState,
      isGetProductsNeedsResolving,
      resetModel,
      gettingUnmappedResources,
      loadUnmappedResources,
    } = get();

    if (func_name !== BubbleDownCase.USER_ID_CHANGE && func_name !== BubbleDownCase.ORG_ID_CHANGE) {
      return;
    }

    const options: UnresolvedResourcesOptions = {
      needs_resolving: isGetProductsNeedsResolving,
      org_id: args[1],
      user_id: args[0],
      ...getUnmappedResourcesDateRange(),
    };

    if (filtersState.operationType !== OperationType.ALL) {
      options.operation_type = filtersState.operationType;
    }

    if (gettingUnmappedResources) return;

    resetModel();
    void loadUnmappedResources(options);
  },
}));
